/**
 * web/src/pages/Login.tsx
 * UI-01: Sign in with Google. Full-viewport centered 380px card on bg-soft.
 * The Sign in button is a full-page redirect (Arctic OAuth needs the
 * browser to follow 302 to Google — fetch can't observe Set-Cookie or
 * follow the cross-origin redirect chain).
 *
 * Banners:
 *  - ?expired=1 → "Your session expired — please sign in again." (amber)
 *  - ?error=ACCESS_DENIED&email=… → "Access pending — contact admin" (red,
 *    Phase 2 D-A01 whitelist auth response).
 *
 * Source: planning-docs/design_handoff_pixel_dashboard/design_files/pages.jsx
 *         lines 839-854 (LoginPage prototype);
 *         design_files/styles.css lines 1300-1440 (.login-page / .login-card / .btn-google).
 *
 * Threat T-05-07-01: ?email param is rendered as a React text node — browser
 * auto-escapes; no dangerouslySetInnerHTML. T-05-07-05: window.location.href
 * uses loginUrl(next), which encodeURIComponents `next` — server-side
 * safeRedirect is the authoritative whitelist.
 */
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/shared/Button';
import { loginUrl } from '../api/auth';

/**
 * Login page (UI-01). Reads three optional query params from the URL:
 *  - `expired=1` to show the session-expired banner
 *  - `error=ACCESS_DENIED` (with optional `email`) to show the access-denied banner
 *  - `next` to round-trip the post-login redirect target through Arctic OAuth
 *
 * @returns the centered login card with Google CTA + optional banners
 * @sideEffects on click of "Continue with Google", performs a full-page
 *              redirect to /auth/login (mutates window.location).
 * @idempotent yes — the page is purely query-string driven; rendering twice
 *             produces identical output.
 */
export function Login() {
  const [params] = useSearchParams();
  const expired = params.get('expired') === '1';
  const errorCode = params.get('error');
  const offendingEmail = params.get('email');
  const next = params.get('next') ?? undefined;

  return (
    <div className="min-h-screen w-full grid place-items-center bg-bg-soft p-6">
      <div className="w-[380px] max-w-full flex flex-col items-center gap-3 px-8 py-9 bg-bg rounded-xl shadow-md border border-border">
        <div
          className="w-10 h-10 rounded-[8px] bg-accent grid place-items-center text-white font-bold text-[16px] -tracking-[0.02em] mb-1"
          aria-hidden="true"
        >
          P
        </div>
        <h1 className="text-[18px] font-semibold -tracking-[0.01em]">Sign in to Pixel</h1>
        <p className="text-[13px] text-text-muted text-center">
          Workflow automation for Pixel Events
        </p>

        {expired && (
          <div
            role="status"
            className="w-full rounded-md border border-waiting-border bg-waiting-soft text-waiting px-3 py-2 text-[12px] font-medium text-center mt-1"
          >
            Your session expired — please sign in again.
          </div>
        )}

        {errorCode === 'ACCESS_DENIED' && (
          <div
            role="alert"
            className="w-full rounded-md border border-failed-border bg-failed-soft text-failed px-3 py-2 text-[12px] font-medium text-center mt-1"
          >
            Access pending — contact admin
            {offendingEmail ? (
              <span className="block opacity-80 mt-0.5">{offendingEmail}</span>
            ) : null}
          </div>
        )}

        <Button
          variant="default"
          size="lg"
          className="w-full justify-center mt-3 gap-3"
          onClick={() => {
            window.location.href = loginUrl(next);
          }}
        >
          <GoogleG />
          Continue with Google
        </Button>

        <p className="text-[11px] text-text-faint text-center mt-3">
          Restricted to authorized Pixel team accounts.
        </p>
      </div>
    </div>
  );
}

/**
 * Google "G" mark — inline SVG (no Lucide equivalent).
 * Decorative; aria-hidden so screen readers don't announce it.
 */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.6 9.2c0-.6-.05-1.18-.15-1.74H9v3.3h4.84c-.21 1.13-.84 2.08-1.78 2.72v2.26h2.88c1.69-1.55 2.66-3.84 2.66-6.54z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.4 0 4.42-.8 5.89-2.16l-2.88-2.26c-.8.54-1.82.86-3.01.86-2.31 0-4.27-1.56-4.97-3.66H1.06v2.3A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M4.03 10.78A5.4 5.4 0 0 1 3.74 9c0-.62.11-1.22.29-1.78V4.92H1.06A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.08l3.07-2.3z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.31 0 2.48.45 3.4 1.34l2.55-2.55C13.42.92 11.4 0 9 0A9 9 0 0 0 1.06 4.92l2.97 2.3C4.73 5.14 6.69 3.58 9 3.58z"
      />
    </svg>
  );
}
